import React from 'react';
import { Helmet } from 'react-helmet';
import Footer from './../../components/Footer';

const RiskTolerance = () => {
  const title = "Risk Toleransı: İdeal Yatırım Portföyünüzü Oluşturmanın Anahtarı | Smart Finans";
  const description = "Risk toleransının ne olduğunu ve yatırım portföyünüzü nasıl etkilediğini öğrenin. Bu makalede, risk toleransınızı değerlendirmenin ve yatırım stratejinizi buna göre şekillendirmenin yollarını keşfedin.";
  const canonicalUrl = "https://smart-portfoy.com/egitim/risk-toleransi";

  const sections = [
    {
      title: 'Risk Toleransı Nedir?',
      description: 'Risk toleransı, bir yatırımcının finansal kayıplara karşı ne kadar dayanıklı olduğunu ve riskli yatırımlara ne kadar açık olduğunu ifade eder.',
      usageInfo: {
        importance: 'Risk toleransı, yatırım kararlarının temelini oluşturur ve yatırımcıların portföylerini nasıl oluşturacaklarını belirler.',
        usage: 'Risk toleransınızı değerlendirerek, yatırım hedeflerinize ve mali durumunuza en uygun yatırım stratejisini belirleyin.'
      }
    },
    {
      title: 'Risk Toleransını Etkileyen Faktörler',
      description: 'Risk toleransını etkileyen çeşitli faktörler vardır:',
      points: [
        'Yaş',
        'Gelir ve mali durum',
        'Yatırım hedefleri',
        'Yatırım süresi',
        'Kişisel risk algısı'
      ]
    },
    {
      title: 'Risk Toleransınızı Nasıl Değerlendirirsiniz?',
      description: 'Risk toleransınızı değerlendirmenin birkaç yolu vardır:',
      points: [
        'Kendi mali durumunuzu ve yatırım hedeflerinizi gözden geçirin.',
        'Risk toleransı anketleri ve testleri kullanın.',
        'Bir finansal danışmanla görüşün.'
      ]
    },
    {
      title: 'Risk Toleransınıza Göre Yatırım Stratejileri',
      description: 'Risk toleransınıza göre uygun yatırım stratejilerini belirleyin:',
      points: [
        'Düşük risk toleransı: Tahviller, mevduat sertifikaları ve para piyasası fonları gibi daha güvenli yatırımlara odaklanın.',
        'Orta risk toleransı: Hisse senetleri ve tahvillerin dengeli bir karışımını içeren çeşitlendirilmiş bir portföy oluşturun.',
        'Yüksek risk toleransı: Hisse senetleri, emtialar ve gayrimenkul gibi daha riskli yatırımlara daha fazla ağırlık verin.'
      ]
    },
    {
      title: 'Risk Toleransınızı Nasıl Artırabilirsiniz?',
      description: 'Risk toleransınızı artırmanın yolları:',
      points: [
        'Finansal bilginizi artırın.',
        'Yatırım deneyiminizi genişletin.',
        'Uzun vadeli yatırım hedeflerine odaklanın.'
      ]
    },
    {
      title: 'Smart Portfoy ile Risk Toleransınızı Değerlendirin',
      description: 'Smart Portfoy ile risk toleransınızı kolayca değerlendirin, otomatik raporlar oluşturun ve yatırım stratejinizi buna göre şekillendirin.',
      points: []
    }
  ];

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta name="keywords" content="risk toleransı, yatırım, finansal analiz, yatırım stratejisi, portföy yönetimi" />
      </Helmet>

      <div className="min-h-screen bg-gray-800 text-gray-100">
        {/* Hero Section */}
        <header className="bg-gradient-to-r from-blue-900 to-indigo-900 py-16">
          <div className="container mx-auto px-4 max-w-6xl">
            <h1 className="text-4xl font-bold mb-4 mt-8 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-indigo-400">
              {title}
            </h1>
            <p className="text-lg text-gray-300 max-w-3xl leading-relaxed">
              {description}
            </p>
          </div>
        </header>

        {/* Image Section with Gradient Overlay */}
        <div className="relative h-48">
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/50 to-gray-900 z-10"></div>
          <img 
            src="/images/blog/risk-tolerance/risk-tolerance-guide.jpeg" 
            alt="Risk Toleransı İllüstrasyonu"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Content Section */}
        <div className="container mx-auto px-4 max-w-6xl py-8">
          <div className="space-y-12">
            {sections.map((section, index) => (
              <section key={index} className="space-y-6 p-6 bg-gray-900/50 rounded-lg hover:bg-blue-900/50 transition duration-300 ease-in-out">
                <h2 className="text-2xl font-bold text-blue-400">{section.title}</h2>
                <p className="text-gray-300">{section.description}</p>
                
                {section.usageInfo && (
                  <div className="space-y-4 mt-4">
                    <div className="bg-gray-800/50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold text-blue-300 mb-2">Önemi</h3>
                      <p className="text-gray-300">{section.usageInfo.importance}</p>
                    </div>
                    
                    <div className="bg-gray-800/50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold text-blue-300 mb-2">Nasıl Kullanılır</h3>
                      <p className="text-gray-300">{section.usageInfo.usage}</p>
                    </div>
                  </div>
                )}

                {section.points && section.points.length > 0 && (
                  <ul className="space-y-2">
                    {section.points.map((point, pIndex) => (
                      <li key={pIndex} className="flex items-start space-x-2">
                        <span className="flex-shrink-0 w-6 h-6 bg-indigo-500 rounded-full flex items-center justify-center text-gray-900 font-bold">
                          {pIndex + 1}
                        </span>
                        <span className="text-gray-300">{point}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
};

export default RiskTolerance;